import type { ThemeChoices } from "$/lib/types/General.types";
import {
  CONTAINER_HEIGHT_PERCENTAGE,
  generateGraphDateRangeFromData,
  generateXAxisTicks,
} from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { numberFormatter } from "$/lib/utils/functions/util.functions";
import type { ReactNode } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  Area,
  AreaChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { AxisDomain } from "recharts/types/util/types";
import { customColors } from "tailwind.config";
import { useScreen } from "usehooks-ts";
import Flexbox from "../../common/Flexbox";
import CustomTooltip from "../LineGraph/CustomTooltip";

type Props = {
  data: Record<string, string | number>[];
  xAxisDataKey: string;
  filtersComponent?: ReactNode;
  unit?: string;
  isStacked?: boolean;
  keys: string[];
  domain?: AxisDomain;
  colors: Record<string, string>;
  theme: ThemeChoices;
  displayNames?: Record<string, string>;
  heightPercent?: number;
  yAxisTicksNumber?: number;
  leftMargin?: number;
  reverseTooltipOrder?: boolean;
};

const MultiAreaGraph = ({
  data,
  xAxisDataKey,
  filtersComponent,
  unit = " MWh",
  keys,
  domain,
  isStacked = false,
  colors,
  theme,
  displayNames,
  heightPercent,
  leftMargin = 0,
  // yAxisTicksNumber,
  reverseTooltipOrder = false,
}: Props) => {
  const { height: screenHeight } = useScreen();

  const graphHeight = useMemo(
    () =>
      heightPercent
        ? Math.round(screenHeight * CONTAINER_HEIGHT_PERCENTAGE) *
          (heightPercent / 100)
        : 400,
    [heightPercent, screenHeight],
  );

  const dateRange = useMemo(
    () => generateGraphDateRangeFromData(data ?? [], "dd/MM/yy", xAxisDataKey),
    [data, xAxisDataKey],
  );

  const xDomain = useMemo(
    () =>
      dateRange && dateRange[0] && dateRange[1]
        ? generateXAxisTicks(dateRange[0], dateRange[1])
        : undefined,
    [dateRange],
  );

  return (
    <Flexbox fullWidth row className="gap-5">
      <ResponsiveContainer width="100%" height={graphHeight}>
        <AreaChart
          data={data}
          margin={{ top: 10, right: 30, left: leftMargin, bottom: 0 }}
        >
          {/* <CartesianGrid strokeDasharray="3 3" /> */}

          <XAxis
            tickMargin={5}
            dataKey={xAxisDataKey}
            fontSize={12}
            interval={0}
            ticks={xDomain}
          />

          <YAxis
            tickSize={2}
            width={85}
            tickMargin={5}
            fontWeight={500}
            fontSize={12}
            unit={unit}
            domain={domain}
            tickFormatter={numberFormatter}
            interval={0}
            // ticks={
            //   Array.isArray(domain)
            //     ? generateYAxisTicks(
            //         Number(domain[0]),
            //         Number(domain[1]),
            //         yAxisTicksNumber,
            //       )
            //     : undefined
            // }
          />

          <Tooltip
            content={
              <CustomTooltip
                unit={unit}
                theme={theme}
                displayNames={displayNames}
                reverseTooltipOrder={reverseTooltipOrder}
              />
            }
            wrapperStyle={{ zIndex: 100 }}
          />

          {keys.map((key) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              stackId={isStacked ? "1" : undefined}
              // stroke="#84BCAF"
              // fill="#84BCAF"
              stroke={colors[key]}
              fill={colors[key]}
              fillOpacity={1}
              connectNulls
            />
          ))}

          {/* <Area
            type="monotone"
            dataKey="uv"
            stroke="#84BCAF"
            fill="#84BCAF"
            fillOpacity={1}
          />
          <Area
            type="monotone"
            dataKey="pv"
            stroke="#F8AE53"
            fill="#F8AE53"
            fillOpacity={1}
          />
          <Area
            type="monotone"
            dataKey="amt"
            stroke="#94AFD8"
            fill="#94AFD8"
            fillOpacity={1}
          /> */}
          <ReferenceLine
            y={0}
            stroke={customColors.tertiary.DEFAULT}
            strokeDasharray={5}
          />
        </AreaChart>
      </ResponsiveContainer>

      {filtersComponent}
    </Flexbox>
  );
};

export default MultiAreaGraph;
